/**
 * Utility to compute display name for job offers in organization schedule/calendar views.
 *
 * For ACCEPTED offers: Shows the accepting provider's full name
 * For PENDING offers: Shows pool name(s) or provider name(s) with truncation
 */

export type JobOfferOrgDisplayInfo = {
  /** Primary display name (provider name if accepted, pool/provider list if pending) */
  primaryName: string;
  /** Secondary info to show below primary (job title) */
  secondaryInfo: string;
  /** Full text for tooltip on hover */
  tooltip: string;
};

type JobOfferForOrgDisplay = {
  title?: string | null;
  decisions?: Array<{
    providerDecision?: boolean | null;
    providerCanceledAt?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  }>;
  userShares?: Array<{
    firstName?: string | null;
    lastName?: string | null;
  }>;
  providerPoolShares?: Array<{
    providerPoolName?: string | null;
  }>;
};

/**
 * Formats a full name from first and last name parts.
 */
function formatFullName(
  firstName?: string | null,
  lastName?: string | null,
): string {
  return `${firstName ?? ""} ${lastName ?? ""}`.trim().replace(/\s+/g, " ");
}

/**
 * Formats a short name (first name + last initial) for truncated display.
 * e.g., "John S."
 */
function formatShortName(
  firstName?: string | null,
  lastName?: string | null,
): string {
  const first = firstName?.trim() || "";
  const lastInitial = lastName?.trim()?.[0] || "";
  if (!first && !lastInitial) {
    return "";
  }
  if (!lastInitial) {
    return first;
  }
  return `${first} ${lastInitial}.`;
}

/**
 * Gets the display information for a job offer in organization views.
 *
 * @param jobOffer - The job offer data containing decisions, shares, and title
 * @returns Display info with primary name, secondary info, and tooltip text
 */
export function getJobOfferOrgDisplayName(
  jobOffer: JobOfferForOrgDisplay,
): JobOfferOrgDisplayInfo {
  const jobTitle = jobOffer.title || "";

  // Find accepted decision (not canceled)
  const acceptedDecision = jobOffer.decisions?.find(
    (d) => d.providerDecision === true && !d.providerCanceledAt,
  );

  // ACCEPTED: Show provider's full name
  if (acceptedDecision) {
    const providerName =
      formatFullName(acceptedDecision.firstName, acceptedDecision.lastName) ||
      "Unnamed Provider";
    return {
      primaryName: providerName,
      secondaryInfo: jobTitle,
      tooltip: jobTitle ? `${providerName} – ${jobTitle}` : providerName,
    };
  }

  // PENDING: Check for pool shares first
  const pools =
    jobOffer.providerPoolShares?.filter((p) => p.providerPoolName) ?? [];
  if (pools.length > 0) {
    const firstPool = pools[0].providerPoolName!;
    if (pools.length === 1) {
      return {
        primaryName: firstPool,
        secondaryInfo: jobTitle,
        tooltip: jobTitle ? `${firstPool} – ${jobTitle}` : firstPool,
      };
    }
    // Multiple pools
    const primaryName = `${firstPool} +${pools.length - 1} more`;
    const allPools = pools.map((p) => p.providerPoolName).join(", ");
    return {
      primaryName,
      secondaryInfo: jobTitle,
      tooltip: jobTitle ? `${allPools} – ${jobTitle}` : allPools,
    };
  }

  // PENDING: Check for individual provider shares
  const providers =
    jobOffer.userShares?.filter(
      (u) => u.firstName?.trim() || u.lastName?.trim(),
    ) ?? [];
  if (providers.length > 0) {
    const firstProvider = providers[0];
    if (providers.length === 1) {
      const providerName =
        formatFullName(firstProvider.firstName, firstProvider.lastName) ||
        "Provider";
      return {
        primaryName: providerName,
        secondaryInfo: jobTitle,
        tooltip: jobTitle ? `${providerName} – ${jobTitle}` : providerName,
      };
    }
    // Multiple providers - use short name + count
    const shortName =
      formatShortName(firstProvider.firstName, firstProvider.lastName) ||
      "Provider";
    const primaryName = `${shortName} +${providers.length - 1} more`;
    const allNames = providers
      .map((p) => formatFullName(p.firstName, p.lastName) || "Provider")
      .join(", ");
    return {
      primaryName,
      secondaryInfo: jobTitle,
      tooltip: jobTitle ? `${allNames} – ${jobTitle}` : allNames,
    };
  }

  // Fallback: No provider info available - show "Pending" as primary
  return {
    primaryName: "Pending",
    secondaryInfo: jobTitle,
    tooltip: jobTitle ? `Pending – ${jobTitle}` : "Pending",
  };
}
